import { ReactNode, Ref } from "react";

import { SkillPanelLogo } from "@/assets/images";
import { useAuthContext } from "@/context/auth";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { cn } from "@/lib/cn";
import { resolveReturnTarget } from "@/lib/return-target";

import { AccountMenu } from "./AccountMenu";

// The header and the content share one column, so the logo sits over the title's left edge and the
// menu over the card's right edge at any window width, instead of drifting to the window's corners
const COLUMN = "mx-auto w-full max-w-xl px-6";

/**
 * Chrome for the account screens. Without it these pages render bare, and someone who has just
 * finished enrolling has nothing to click.
 */
export const AccountShell = ({
  title,
  headingRef,
  children,
}: {
  title: string;
  /** Lets a multi-step screen move focus to its title when the step changes */
  headingRef?: Ref<HTMLHeadingElement>;
  children: ReactNode;
}) => {
  const { user } = useAuthContext();
  const target = resolveReturnTarget(document.referrer, user?.products ?? []);
  useDocumentTitle(title);

  return (
    <div className="front-door min-h-screen bg-gradient-panel">
      <header
        className={cn(
          COLUMN,
          "flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-4 sm:gap-x-6",
        )}
      >
        {/* The logo takes the same way back as the link beside it, by mouse or touch only: it is out
            of the tab order and the accessibility tree, so the visible "Back" link is the one way
            back for a keyboard or a screen reader instead of two links to one place under two
            names. It is a header mark here, not the sign-in page's centrepiece, so it stays small
            enough to share one row with the link and the menu on a phone */}
        <a
          href={target.url}
          tabIndex={-1}
          aria-hidden="true"
          className="inline-flex min-h-11 items-center"
        >
          <SkillPanelLogo className="h-5 w-auto sm:h-6" aria-hidden />
        </a>
        <div className="flex items-center gap-4 text-sm sm:gap-6">
          <a
            href={target.url}
            className="inline-flex items-center font-bold text-primary-fg hover:underline pointer-coarse:min-h-11"
          >
            {/* On a phone the label shortens to "Back" so the header keeps one row; screen
                readers still hear where it leads */}
            ← Back <span className="max-sm:sr-only">to {target.label}</span>
          </a>
          <AccountMenu />
        </div>
      </header>
      <main className={cn(COLUMN, "pb-16 pt-4")}>
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="mb-6 text-2xl font-bold tracking-tight text-brand focus-visible:outline-hidden"
        >
          {title}
        </h1>
        {children}
      </main>
    </div>
  );
};
